/**
 * Global keyboard shortcuts for the project board.
 *
 * - `C`  → create a task in the first column
 * - `J / K` → next / previous task in the list view
 * - `E`  → open the focused task
 * - `1..5` → move the focused task to column N
 * - `?`  → show the shortcut cheatsheet
 *
 * Shortcuts are no-ops when an editable element is focused.
 */

import { useEffect } from 'react'
import type { ProjectColumn, Task } from '@friday/shared/realtime'
import { toast } from '@/features/feedback/toast'
import { openCommandPalette } from '@/components/shell/CommandPalette'

interface ShortcutOpts {
  columns: ProjectColumn[]
  tasks: Task[]
  focusedTaskId: string | null
  setFocusedTaskId: (id: string | null) => void
  openTask: (t: Task) => void
  moveTask: (input: { taskId: string; columnId: string; version: number }) => void
  createTask: (title: string, columnId: string) => void
}

function isEditable(t: EventTarget | null): boolean {
  if (!t || !(t instanceof HTMLElement)) return false
  const tag = t.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if (t.isContentEditable) return true
  return false
}

export function useProjectShortcuts({
  columns,
  tasks,
  focusedTaskId,
  setFocusedTaskId,
  openTask,
  moveTask,
  createTask,
}: ShortcutOpts) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // ⌘K — palette, always
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        openCommandPalette()
        return
      }
      if (isEditable(e.target)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      const key = e.key.toLowerCase()
      if (key === '?') {
        e.preventDefault()
        toast('Shortcuts: C create · J/K next/prev · E open · 1-5 move · ⌘K palette', { kind: 'info' })
        return
      }
      if (key === 'c') {
        e.preventDefault()
        const col = columns[0]
        if (col) {
          const title = window.prompt('New task title')?.trim()
          if (title) createTask(title, col.id)
        }
        return
      }
      if (key === 'j' || key === 'k') {
        e.preventDefault()
        const idx = focusedTaskId ? tasks.findIndex((t) => t.id === focusedTaskId) : -1
        const next = key === 'j'
          ? Math.min(tasks.length - 1, idx + 1)
          : Math.max(0, idx - 1)
        const t = tasks[next]
        if (t) {
          setFocusedTaskId(t.id)
          document.querySelector(`[data-task-id="${t.id}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
        return
      }
      if (key === 'e') {
        e.preventDefault()
        const t = focusedTaskId ? tasks.find((x) => x.id === focusedTaskId) : null
        if (t) openTask(t)
        return
      }
      if (/^[1-5]$/.test(e.key) && columns.length >= Number(e.key)) {
        if (!focusedTaskId) return
        const t = tasks.find((x) => x.id === focusedTaskId)
        const col = columns[Number(e.key) - 1]
        if (!t || !col) return
        e.preventDefault()
        if (t.columnId !== col.id) {
          moveTask({ taskId: t.id, columnId: col.id, version: t.version })
        }
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [columns, tasks, focusedTaskId, setFocusedTaskId, openTask, moveTask, createTask])
}
